<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
    title: {
        type: String,
        default: '',
    },
    showClose: {
        type: Boolean,
        default: true,
    },
    contentClass: {
        type: String,
        default: '',
    },
    headerClass: {
        type: String,
        default: '',
    },
    bodyClass: {
        type: String,
        default: '',
    },
    footerClass: {
        type: String,
        default: '',
    },
    teleportTo: {
        type: String,
        default: 'body',
    },
    ariaLabel: {
        type: String,
        default: '',
    },
    closeOnBackdrop: {
        type: Boolean,
        default: true,
    },
    closeOnEscape: {
        type: Boolean,
        default: true,
    },
})

const emit = defineEmits(['close'])

const modalContentRef = ref(null)
const previouslyFocused = ref(null)

const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(', ')

function getFocusable() {
    return modalContentRef.value
        ? Array.from(modalContentRef.value.querySelectorAll(FOCUSABLE))
        : []
}

function trapFocus(e) {
    if (e.key !== 'Tab') return
    const focusable = getFocusable()
    if (!focusable.length) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (e.shiftKey) {
        if (document.activeElement === first) {
            e.preventDefault()
            last.focus()
        }
    } else {
        if (document.activeElement === last) {
            e.preventDefault()
            first.focus()
        }
    }
}

function handleEscape(e) {
    if (e.key === 'Escape' && props.closeOnEscape) {
        emit('close')
    }
}

onMounted(() => {
    previouslyFocused.value = document.activeElement
    document.addEventListener('keydown', trapFocus)
    document.addEventListener('keydown', handleEscape)
    // focus first focusable element
    const focusable = getFocusable()
    if (focusable.length) focusable[0].focus()
})

onBeforeUnmount(() => {
    document.removeEventListener('keydown', trapFocus)
    document.removeEventListener('keydown', handleEscape)
    if (previouslyFocused.value) previouslyFocused.value.focus()
})
</script>

<template>
    <Teleport :to="teleportTo">
        <div class="modal-overlay" @click.self="closeOnBackdrop && $emit('close')">
            <div
                ref="modalContentRef"
                class="modal-content"
                :class="contentClass"
                role="dialog"
                aria-modal="true"
                :aria-label="ariaLabel || title || undefined"
            >
                <div class="modal-header" :class="headerClass">
                    <slot name="header">
                        <h2 class="modal-header-title">{{ title }}</h2>
                        <button v-if="showClose" class="modal-close" type="button" aria-label="Sluiten" @click="$emit('close')">
                            <XMarkIcon class="w-6 h-6" />
                        </button>
                    </slot>
                </div>
                <div class="modal-body" :class="bodyClass">
                    <slot />
                </div>
                <div v-if="$slots.footer" class="modal-footer" :class="footerClass">
                    <slot name="footer" />
                </div>
            </div>
        </div>
    </Teleport>
</template>

<style scoped>
.modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: clamp(16px, 4vw, 32px);
    background: rgba(15, 23, 42, 0.42);
}

.modal-content {
    width: min(100%, 520px);
    max-height: min(760px, calc(100vh - 32px));
    overflow: hidden;
    display: flex;
    flex-direction: column;
    border-radius: var(--radius-lg);
    border: 1px solid rgba(var(--coral-rgb), 0.14);
    background: var(--surface-card, #fff);
    box-shadow: 0 24px 70px rgba(15, 23, 42, 0.22);
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--sp-2);
    padding: var(--sp-3) var(--sp-4) var(--sp-2);
    border-bottom: 1px solid rgba(var(--ink-rgb, 30 41 59), 0.08);
}

.modal-header-title {
    margin: 0;
    color: var(--ink);
    font-size: var(--font-xl);
    line-height: 1.2;
}

.modal-close {
    width: 36px;
    height: 36px;
    min-height: 0;
    padding: 0;
    display: grid;
    place-items: center;
    border: 1px solid rgba(var(--ink-rgb, 30 41 59), 0.1);
    border-radius: var(--radius-full);
    background: var(--white);
    color: var(--muted);
    cursor: pointer;
}

.modal-close:hover {
    border-color: rgba(var(--coral-rgb), 0.28);
    color: var(--coral);
    background: rgba(var(--coral-rgb), 0.06);
}

.modal-close svg {
    width: 20px;
    height: 20px;
}

.modal-body {
    padding: var(--sp-3) var(--sp-4);
    overflow: auto;
}

.modal-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: var(--sp-1-5);
    padding: var(--sp-2) var(--sp-4) var(--sp-3);
    border-top: 1px solid rgba(var(--ink-rgb, 30 41 59), 0.08);
}

@media (max-width: 520px) {
    .modal-overlay {
        align-items: flex-end;
        padding: 12px;
    }

    .modal-content {
        max-height: calc(100vh - 24px);
        border-radius: var(--radius-lg) var(--radius-lg) var(--radius-md) var(--radius-md);
    }

    .modal-header,
    .modal-body,
    .modal-footer {
        padding-left: var(--sp-2);
        padding-right: var(--sp-2);
    }
}
</style>
